import { useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { NewTabLink } from "components/NewTabLink";
import {
  ClientAppEvent,
  SHARE_LIVE_SHIPMENT_TRACKING_BLOG_URL,
} from "constants/index";
import { useRoleContext } from "hooks";
import { buildPublicShipmentUrl } from "utils";

interface Props {
  open: boolean;
  onClose: () => void;
  publicToken: string;
  shipmentId: number;
  source: "shipper" | "carrier" | "public";
}

export function PublicShipmentShareModal(props: Props) {
  const { open, onClose, publicToken, shipmentId, source } = props;
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const posthog = usePostHog();
  const { role } = useRoleContext();

  const shareUrl = buildPublicShipmentUrl(publicToken);

  // The public token is a share credential; never include it (or the share URL) in analytics.
  const shareEventProperties = {
    shipment_id: shipmentId,
    source,
    triggered_by_role: role?.id,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      posthog?.capture(
        ClientAppEvent.PUBLIC_SHIPMENT_LINK_COPIED,
        shareEventProperties,
      );
    } catch {
      // No-op: clipboard write can fail in non-secure contexts or when permission is denied.
    }
  };

  const handleOpenLink = () => {
    posthog?.capture(
      ClientAppEvent.PUBLIC_SHIPMENT_LINK_OPENED,
      shareEventProperties,
    );
  };

  const handleOpenSharingHelp = () => {
    posthog?.capture(
      ClientAppEvent.PUBLIC_SHIPMENT_SHARING_HELP_OPENED,
      shareEventProperties,
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Share shipment</DialogTitle>
        <DialogContent dividers>
          <Stack direction="column" spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Anyone with this link can view the shipment&apos;s status, route,
              and ETAs. No account or login required.
            </Typography>

            <Stack direction="column" spacing={1}>
              <Typography variant="body2" fontWeight="bold">
                Public tracking link
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={shareUrl}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  onFocus={(event) => event.target.select()}
                />
                <Tooltip title="Copy the public tracking link to your clipboard">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyLink}
                    sx={{ textTransform: "none", flexShrink: 0 }}
                  >
                    Copy
                  </Button>
                </Tooltip>
                <Tooltip title="Open the public tracking link in a new tab">
                  <Button
                    component="a"
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    color="primary"
                    endIcon={<OpenInNewIcon />}
                    onClick={handleOpenLink}
                    sx={{ textTransform: "none", flexShrink: 0 }}
                  >
                    Open
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              <NewTabLink
                href={SHARE_LIVE_SHIPMENT_TRACKING_BLOG_URL}
                onClick={handleOpenSharingHelp}
              >
                Find out more about shipment sharing
              </NewTabLink>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Tooltip title="Close this share dialog">
            <Button onClick={onClose} sx={{ textTransform: "none" }}>
              Close
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setLinkCopied(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Link copied to clipboard.
        </Alert>
      </Snackbar>
    </>
  );
}
